import ErrorBanner from "components/ui/ErrorBanner";
import {
  DICTIONARY_MODES,
  DictionaryMode,
  DictionaryToolsContext,
  DictionaryToolsContextValue,
} from "contexts/DictionaryToolsContext";
import { ErrorBoundary } from "components/ErrorBoundary";
import TalaeraSpinner from "components/TalaeraSpinner/TalaeraSpinner";
import { PropsWithChildren, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useUser } from "contexts/UserContext";
import { useRouteMeta } from "utils/routing";
import Tabs from "components/ui/Tabs";
import SearchInputWithSuggestions from "./SearchInputWithSuggestions/SearchInputWithSuggestions";
import DefineTab from "./DefineTab";
import SaveTab from "./SaveTab";
import Sidebar from "./Sidebar";
import TextSelectionMenu from "./TextSelectionMenu/TextSelectionMenu";
import TranslateTab from "./TranslateTab";
import { useDictionarySearch } from "./useDictionarySearch";
import "./tabs.css";
import "./DictionaryTools.css";
import type { TranslationKey } from "services/i18n/keys";

const LOOKUP_FAILED_TEXT = "Dictionary.panel.lookupFailed";

const MODE_LABEL_KEYS: Record<DictionaryMode, TranslationKey> = {
  define: "Dictionary.panel.modeDefine",
  translate: "Dictionary.panel.modeTranslate",
  save: "Dictionary.panel.modeMyVocab",
};

export default function DictionaryTools(props: PropsWithChildren) {
  const { t } = useTranslation();
  const user = useUser(false);
  const enabled = useRouteMeta().tools && user?.canUseDictionaryTools === true;

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const {
    text,
    setText,
    mode,
    onModeChanged,
    onSearch,
    applyLookup,
    myWordsSearchText,
    clearMyWordsFilter,
    result,
    isError,
    isFetching,
  } = useDictionarySearch();

  const openSidebar = useCallback(
    (openMode: DictionaryMode, selectionText: string) => {
      if (!selectionText) {
        return;
      }
      setIsSidebarOpen(true);
      applyLookup(openMode, selectionText);
    },
    [applyLookup]
  );

  const closeSidebar = useCallback(() => setIsSidebarOpen(false), []);

  const toggleSidebar = useCallback(() => setIsSidebarOpen(prev => !prev), []);

  const onClose = () => {
    clearMyWordsFilter();
    closeSidebar();
  };

  const contextValue = useMemo<DictionaryToolsContextValue>(
    () => ({
      enabled,
      isSidebarOpen,
      openSidebar,
      toggleSidebar,
      closeSidebar,
    }),
    [enabled, isSidebarOpen, openSidebar, toggleSidebar, closeSidebar]
  );

  const defineContent = isError ? t(LOOKUP_FAILED_TEXT) : result;

  let activeTab = null;
  switch (mode) {
    case "define":
      activeTab = <DefineTab defineContent={defineContent} />;
      break;
    case "translate":
      activeTab = <TranslateTab searchText={text} />;
      break;
    case "save":
      activeTab = (
        <SaveTab
          myWordsSearchText={myWordsSearchText}
          onModeChanged={onModeChanged}
          onTextChanged={setText}
        />
      );
      break;
    default:
  }

  const sidebarPanel = (
    <ErrorBoundary
      fallback={<ErrorBanner>{t("Dictionary.panel.genericError")}</ErrorBanner>}
    >
      <div className="dictionary-tools-panel">
        <div className="dictionary-header">
          <h2>{t("Dictionary.panel.title")}</h2>
          <button
            type="button"
            className="close-action"
            onClick={onClose}
            aria-label={t("Common.actions.close")}
          >
            ✕
          </button>
        </div>

        <div className="dictionary-content">
          <SearchInputWithSuggestions
            value={text}
            onValueChanged={setText}
            onSearch={onSearch}
          />
          <div className="dictionary-tools-result-panel">
            <Tabs
              className="dictionary-mode-tabs"
              aria-label={t("Dictionary.panel.title")}
              items={DICTIONARY_MODES.map(m => ({
                value: m,
                label: t(MODE_LABEL_KEYS[m]),
              }))}
              value={mode}
              onChange={value => onModeChanged(value as DictionaryMode)}
            />
            {isFetching && (
              <div className="spinner-container">
                <TalaeraSpinner />
              </div>
            )}
            {!isFetching && activeTab}
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );

  return (
    <DictionaryToolsContext.Provider value={contextValue}>
      <Sidebar enabled={enabled} isOpen={isSidebarOpen} sidebarContent={sidebarPanel}>
        {props.children}
      </Sidebar>
      {enabled && <TextSelectionMenu />}
    </DictionaryToolsContext.Provider>
  );
}
